import { ActionContext, ActionTree } from "vuex";
import { IMyBookingsState } from "@/core/interfaces/state/myBookingsState.interface";
import { IMyBookingsFlight } from "@/core/interfaces/state/flightBookings.interface";
import MyBookingsService from "@/modules/MyBookings/services/myBookings.service";
import { FetchBookingsParamsRequestModel } from "@/modules/MyBookings/models/fetchBookingsParams.request";
import { SendAirlineTicketBodyRequest } from "@/modules/MyBookings/models/sendAirlineTicketBody.request";
import { IMyBookingsHotel } from "@/core/interfaces/state/hotelBookings.interface";
import { IMyBookingsTour } from "@/core/interfaces/state/tourBookings.interface";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { StatusCodes } from "http-status-codes";
import { StoreState } from "@/store/type";
import { ICoreErrorResponse } from "@/core/interfaces/coreResponse.interface";

const actions: ActionTree<IMyBookingsState, StoreState> = {
  async fetchFlightBookings(
    context: ActionContext<IMyBookingsState, StoreState>,
    {
      payload,
      isFetchNewData,
      callBack,
    }: {
      payload: FetchBookingsParamsRequestModel;
      isFetchNewData: boolean;
      callBack?: () => void;
    },
  ) {
    context.commit("enableIsFetchFlightBookings");
    const myBookingsService = new MyBookingsService();

    const response: IMyBookingsFlight =
      await myBookingsService.fetchFlightBookings(payload, isFetchNewData);
    context.commit("saveFlightBookings", response);
    if (callBack) {
      callBack();
    }
  },
  async downloadBrandTicket(
    context: ActionContext<IMyBookingsState, StoreState>,
    payload: { bookingId: string; pnr: string },
  ) {
    try {
      context.commit("enableIsDownloadingBrandTicket");
      const myBookingsService = new MyBookingsService();

      await myBookingsService.dowloadBrandTicket(
        payload.bookingId,
        payload.pnr,
      );

      notificationService.type = NOTIFICATION_TYPES.SUCCESS;
      notificationService.description =
        NOTIFICATION_MESSAGES.BRAND_TICKET_DOWNLOAD_SUCCESS;
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      notificationService.triggerNotification();
      context.commit("disableIsDownloadingBrandTicket");
    }
  },
  async downloadTourVoucher(
    context: ActionContext<IMyBookingsState, StoreState>,
    bookingId: string,
  ) {
    context.commit("enableIsDownloadingTourVoucher");
    const myBookingsService = new MyBookingsService();

    await myBookingsService.downloadTourVoucher(bookingId);
    context.commit("disableIsDownloadingTourVoucher");
  },
  async sendAirlineTicket(
    context: ActionContext<IMyBookingsState, StoreState>,
    {
      payload,
      callback,
    }: { payload: SendAirlineTicketBodyRequest; callback: () => void },
  ) {
    context.commit("enableIsSendingAirlineTicket");
    const myBookingsService = new MyBookingsService();
    await myBookingsService.sendAirlineTicket(payload);
    context.commit("disableIsSendingAirlineTicket");
    callback();
  },

  async fetchHotelBookings(
    context: ActionContext<IMyBookingsState, StoreState>,
    {
      payload,
      callback,
    }: { payload: FetchBookingsParamsRequestModel; callback: () => void },
  ) {
    context.commit("enableIsFetchHotelBookings");
    const myBookingsService = new MyBookingsService();

    const response: IMyBookingsHotel =
      await myBookingsService.fetchHotelBookings(payload);
    context.commit("saveHotelBookings", response);
    if (callback) {
      callback();
    }
  },
  async downloadHotelVoucher(
    context: ActionContext<IMyBookingsState, StoreState>,
    bookingId: string,
  ) {
    try {
      context.commit("enableIsDownloadingMBHotelVoucher", true);

      const hotelService = new MyBookingsService();
      const response = await hotelService.downloadVoucher(bookingId);

      if (
        response.success &&
        response?.status &&
        response.status >= StatusCodes.OK
      ) {
        const blob = new Blob([response.data]);
        const blobUrl = URL.createObjectURL(blob);

        context.commit("saveVoucherUrl", blobUrl);

        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response?.data?.message || "Voucher downloaded successfully";
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      notificationService.triggerNotification();
      context.commit("disableIsDownloadingMBHotelVoucher", false);
    }
  },
  async fetchTourBookings(
    context: ActionContext<IMyBookingsState, StoreState>,
    {
      payload,
      callback,
    }: { payload: FetchBookingsParamsRequestModel; callback: () => void },
  ) {
    context.commit("enableIsFetchTourBookings");
    const myBookingsService = new MyBookingsService();

    const response: IMyBookingsTour =
      await myBookingsService.fetchTourBookings(payload);
    context.commit("saveTourBookings", response);
    if (callback) {
      callback();
    }
  },
};

export default actions;
